import React from 'react';
import { Field } from './Modal';

/**
 * "Repeating list" fields — a question answered with any number of rows, each row
 * having the same columns (e.g. an equipment survey: reference, name, make, serial).
 *
 *   <RepeatingListInput />   fills the list on site (one card per row — works at phone width)
 *   <RepeatingColumnsEditor> sets the columns in the form builder
 *   repeatingSummary()       the one-line text stored as the answer's value
 *
 * Values are an array of row objects keyed by column id. The submitted answer also
 * carries `rows` and `columns`, which the server turns into one report row per cell
 * and can map into the asset register.
 */
export const REPEATING_TYPE = 'Repeating list';
export const COLUMN_TYPES = ['Text', 'Number', 'Date', 'Yes / No', 'Dropdown'];
export const MAX_ROWS = 200;

const newColId = () => `col_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const optionsOf = (c) => (Array.isArray(c.options) ? c.options : String(c.options || '').split(',')).map((o) => String(o).trim()).filter(Boolean);

/** Normalise stored columns: every column has an id and a label. */
export const columnsOf = (field) => (Array.isArray(field && field.columns) ? field.columns : [])
  .filter((c) => c && typeof c === 'object')
  .map((c, j) => ({ ...c, id: c.id != null && String(c.id).trim() ? String(c.id) : `c${j}`, label: c.label || `Column ${j + 1}`, type: c.type || 'Text' }));

/** Rows that have at least one answered cell. */
export const filledRows = (value, columns) => (Array.isArray(value) ? value : [])
  .filter((r) => r && typeof r === 'object' && columns.some((c) => String(r[c.id] ?? '').trim() !== ''));

/** "2 rows — AB12 · Pump; AB13 · Fan" (first two columns of up to five rows). */
export const repeatingSummary = (value, columns) => {
  const rows = filledRows(value, columns);
  if (!rows.length) return '';
  const head = columns.slice(0, 2);
  const parts = rows.slice(0, 5).map((r) => head.map((c) => String(r[c.id] ?? '').trim()).filter(Boolean).join(' · ')).filter(Boolean);
  return `${rows.length} row${rows.length === 1 ? '' : 's'}${parts.length ? ` — ${parts.join('; ')}${rows.length > 5 ? '; …' : ''}` : ''}`;
};

const CellInput = ({ col, value, onChange, id }) => {
  if (col.type === 'Number') return <input id={id} className="cw-input" type="number" inputMode="decimal" step="any" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  if (col.type === 'Date') return <input id={id} className="cw-input" type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  if (col.type === 'Yes / No' || col.type === 'Dropdown') {
    const opts = col.type === 'Yes / No' ? ['Yes', 'No'] : optionsOf(col);
    return (
      <select id={id} className="cw-input" value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input id={id} className="cw-input" value={value || ''} onChange={(e) => onChange(e.target.value)} maxLength={500} />;
};

/** On-site input: add, fill and remove rows. */
const RepeatingListInput = ({ field, value, onChange, invalid, id, describedBy }) => {
  const columns = columnsOf(field);
  const rows = Array.isArray(value) ? value : [];
  const setCell = (ri, colId, v) => onChange(rows.map((r, i) => (i === ri ? { ...r, [colId]: v } : r)));
  const addRow = () => { if (rows.length < MAX_ROWS) onChange([...rows, {}]); };
  const removeRow = (ri) => onChange(rows.filter((_, i) => i !== ri));
  const base = id || `rl-${field && field.id}`;

  if (!columns.length) return <p className="cw-meta" style={{ margin: 0 }}>No columns have been set up for this list.</p>;
  return (
    <div id={id} role="group" aria-describedby={describedBy}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, border: invalid ? '1px solid var(--cw-danger, #dc2626)' : 'none', borderRadius: 10, padding: invalid ? 8 : 0 }}>
      {rows.length === 0 && <p className="cw-meta" style={{ margin: 0 }}>No rows yet — add one for each item.</p>}
      {rows.map((r, ri) => (
        <fieldset key={ri} className="cw-card" style={{ margin: 0, padding: '10px 12px', border: '1px solid var(--cw-border)' }}>
          <legend className="cw-label" style={{ padding: '0 4px', margin: 0 }}>Row {ri + 1}</legend>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 8 }}>
            {columns.map((c) => (
              <div key={c.id} className="cw-field" style={{ margin: 0 }}>
                <label className="cw-label" htmlFor={`${base}-${ri}-${c.id}`}>{c.label}</label>
                <CellInput id={`${base}-${ri}-${c.id}`} col={c} value={r[c.id]} onChange={(v) => setCell(ri, c.id, v)} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" className="cw-btn-link danger" onClick={() => removeRow(ri)}>Remove row<span className="cw-sr-only"> {ri + 1}</span></button>
          </div>
        </fieldset>
      ))}
      <div>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={addRow} disabled={rows.length >= MAX_ROWS}>
          <i className="fa-solid fa-plus" aria-hidden="true" /> Add row
        </button>
        {rows.length > 0 && <span className="cw-meta" style={{ marginLeft: 10 }}>{rows.length} row{rows.length === 1 ? '' : 's'}</span>}
      </div>
    </div>
  );
};

/** Builder: the list's columns (label, type, options for a dropdown). */
export const RepeatingColumnsEditor = ({ columns, onChange, fieldName = 'this list' }) => {
  const cols = Array.isArray(columns) ? columns : [];
  const set = (j, patch) => onChange(cols.map((c, k) => (k === j ? { ...c, ...patch } : c)));
  const move = (j, dir) => { const k = j + dir; if (k < 0 || k >= cols.length) return; const n = cols.slice(); [n[j], n[k]] = [n[k], n[j]]; onChange(n); };
  return (
    <fieldset style={{ border: '1px solid var(--cw-border-light)', borderRadius: 8, padding: '8px 10px', margin: 0, background: 'var(--cw-surface-alt, #fbfcfe)' }}>
      <legend className="cw-label" style={{ padding: '0 4px' }}>Columns in {fieldName}</legend>
      {cols.length === 0 && <p className="cw-meta" style={{ margin: '0 0 8px' }}>Add a column for each thing recorded per row — for an equipment list: reference, name, make, serial number.</p>}
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {cols.map((c, j) => (
          <li key={c.id || j} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="cw-icon-btn" aria-label={`Move column ${c.label || j + 1} up`} onClick={() => move(j, -1)} disabled={j === 0}><i className="fa-solid fa-chevron-up" aria-hidden="true" /></button>
            <label className="cw-sr-only" htmlFor={`col-l-${c.id}`}>Column {j + 1} name</label>
            <input id={`col-l-${c.id}`} className="cw-input" style={{ flex: '1 1 140px', minWidth: 0 }} value={c.label || ''} placeholder={`Column ${j + 1}`} maxLength={120}
              onChange={(e) => set(j, { label: e.target.value })} />
            <label className="cw-sr-only" htmlFor={`col-t-${c.id}`}>Column {j + 1} type</label>
            <select id={`col-t-${c.id}`} className="cw-input" style={{ width: 120 }} value={c.type || 'Text'} onChange={(e) => set(j, { type: e.target.value })}>
              {COLUMN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button type="button" className="cw-icon-btn" aria-label={`Remove column ${c.label || j + 1}`} title="Remove column" onClick={() => onChange(cols.filter((_, k) => k !== j))}>
              <i className="fa-solid fa-trash" aria-hidden="true" style={{ color: 'var(--cw-danger-fg)' }} />
            </button>
            {c.type === 'Dropdown' && (
              <div style={{ flex: '1 1 100%' }}>
                <Field label={`Options for ${c.label || `column ${j + 1}`} (comma-separated)`}>
                  <input value={Array.isArray(c.options) ? c.options.join(', ') : (c.options || '')} onChange={(e) => set(j, { options: e.target.value })} placeholder="e.g. Good, Fair, Poor" />
                </Field>
              </div>
            )}
          </li>
        ))}
      </ol>
      <button type="button" className="cw-btn-link" style={{ marginTop: 6 }} onClick={() => onChange([...cols, { id: newColId(), label: '', type: 'Text' }])}>+ Add column</button>
    </fieldset>
  );
};

/** Builder columns → stored columns (blank ones dropped, dropdown options as a list). */
export const toStoredColumns = (columns) => (Array.isArray(columns) ? columns : [])
  .filter((c) => c && String(c.label || '').trim())
  .map((c) => ({
    id: c.id || newColId(), label: String(c.label).trim(), type: COLUMN_TYPES.includes(c.type) ? c.type : 'Text',
    ...(c.key ? { key: c.key } : {}),
    ...(c.type === 'Dropdown' ? { options: optionsOf(c) } : {}),
  }));

export default RepeatingListInput;
